import { Ref, useRef } from 'react';
import { VisuallyHidden, mergeProps, useFocusRing, useSwitch } from 'react-aria';
import { useToggleState } from 'react-stately';

import { TooltipTriggerProps, pickTooltipTriggerProps } from '~/components/scales/Tooltip';
import { StrictUnion } from '~/utilities/types';

type BaseProps = TooltipTriggerProps & {
  'data-cy'?: string;
  id?: string;
  isDisabled?: boolean;
  isFullWidth?: boolean;
  isSelected?: boolean;
  onChange: (isSelected: boolean) => void;
  ref?: Ref<HTMLLabelElement>;
  type?: 'toolbar' | 'default';
};

type LabelledProps = {
  'aria-label'?: string;
  label: string;
};

type UnlabelledProps = {
  'aria-label': string;
  label?: string;
};

type Props = BaseProps & StrictUnion<LabelledProps | UnlabelledProps>;

export default function Switch(props: Props) {
  const toggleProps = {
    isDisabled: props.isDisabled,
    isSelected: props.isSelected ?? false,
    onChange: props.onChange,
  };

  const state = useToggleState(toggleProps);
  const ref = useRef(null);
  const {
    inputProps: { id, ...inputProps },
  } = useSwitch(
    {
      ...toggleProps,
      'aria-label': props['aria-label'],
      children: props.label,
      id: props.id,
    },
    state,
    ref
  );

  const { focusProps, isFocusVisible } = useFocusRing();

  return (
    <label
      {...pickTooltipTriggerProps(props)}
      ref={props.ref}
      className={[
        'group relative inline-flex h-10 cursor-pointer items-center gap-2 rounded',
        props.isFullWidth ? 'w-full' : '',
        props.type === 'toolbar' ? 'px-2' : '',
      ].join(' ')}
      data-cy={props['data-cy']}
      id={id}
    >
      <Label isDisabled={props.isDisabled} label={props.label} type={props.type} />
      <VisuallyHidden className="left-0 top-0">
        <input {...mergeProps(inputProps, focusProps)} ref={ref} />
      </VisuallyHidden>
      <Toggle
        isDisabled={props.isDisabled}
        isFocusVisible={isFocusVisible}
        isSelected={state.isSelected}
      />
    </label>
  );
}

function Label(props: { label?: string; isDisabled?: boolean; type?: 'toolbar' | 'default' }) {
  if (!props.label) return null;
  return (
    <div
      className={[
        props.type === 'toolbar' ? '!font-bold type-body2' : 'type-body1',
        props.isDisabled ? 'text-type-inactive' : 'text-type-primary',
      ].join(' ')}
    >
      {props.label}
    </div>
  );
}

function Toggle(props: { isDisabled?: boolean; isFocusVisible: boolean; isSelected?: boolean }) {
  let containerClassName = '';
  let switchClassName = '';
  if (props.isDisabled) {
    containerClassName = 'bg-switch-disabled-off';
    switchClassName = props.isSelected ? 'bg-switch-disabled-on' : 'bg-switch-disabled-off';
  } else {
    containerClassName = props.isSelected
      ? 'bg-switch-track-active'
      : 'bg-switch-track-off group-hover:bg-switch-track-active';
    switchClassName = props.isSelected
      ? 'bg-switch-knob-active'
      : 'bg-switch-knob-off group-hover:bg-switch-hover';
  }

  return (
    <div
      className={`${containerClassName} relative ml-auto h-[10px] w-8 shrink-0 rounded-2xl transition-all`}
    >
      <div
        className={[
          'absolute -top-[3px] h-4 w-4 rounded-full shadow transition-[left]',
          switchClassName,
          props.isSelected
            ? 'left-[calc(100%-1rem)]' /* it's calc(100% - the-width-of-the-switch) */
            : 'left-0',
          props.isFocusVisible ? 'outline outline-4' : '',
        ].join(' ')}
      />
    </div>
  );
}
